/**
 * Credits Management Component
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag, Download, Mail, MapPin, Calendar, DollarSign } from "lucide-react";
import { authAxios } from "@/utils/auth";

export default function CreditsManagement() {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [creditStats, setCreditStats] = useState(null);

  const fetchCredits = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await authAxios.get(`/credits${params}`);
      setCredits(res.data);
    } catch {
      toast.error("Failed to load credits");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchCreditStats = useCallback(async () => {
    try {
      const res = await authAxios.get("/credits/stats/summary");
      setCreditStats(res.data);
    } catch (error) {
      // Stats are non-critical - log but don't show toast
      console.warn("Failed to load credit stats:", error.message);
    }
  }, []);

  useEffect(() => {
    fetchCredits();
    fetchCreditStats();
  }, [fetchCredits, fetchCreditStats]);

  const exportCredits = useCallback(async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await authAxios.get(`/credits/export/csv${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `credits_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Credits exported successfully");
    } catch {
      toast.error("Failed to export credits");
    }
  }, [statusFilter]);

  const expireCredit = useCallback(async (creditId) => {
    try {
      await authAxios.post(`/credits/${creditId}/expire`);
      toast.success("Credit expired");
      fetchCredits();
      fetchCreditStats();
    } catch {
      toast.error("Failed to expire credit");
    }
  }, [fetchCredits, fetchCreditStats]);

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-emerald-100 text-emerald-800",
      used: "bg-blue-100 text-blue-800",
      expired: "bg-red-100 text-red-800",
    };
    return <Badge className={styles[status] || styles.active}>{status}</Badge>;
  };

  return (
    <div data-testid="credits-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Credit Codes
        </h1>
        <div className="flex gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="credit-status-filter">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Credits</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="used">Used</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportCredits} variant="outline" data-testid="export-credits-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {creditStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Credits</p>
              <p className="text-2xl font-bold text-slate-900">{creditStats.total_credits}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Active</p>
              <p className="text-2xl font-bold text-emerald-600">{creditStats.by_status?.active || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Used</p>
              <p className="text-2xl font-bold text-blue-600">{creditStats.by_status?.used || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Active Value</p>
              <p className="text-2xl font-bold text-green-600">${creditStats.value?.active_total || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : credits.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No credits found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {credits.map((credit) => (
            <Card key={credit.id} className="border-slate-200" data-testid={`credit-card-${credit.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <code className="text-lg font-mono font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded">
                        {credit.code}
                      </code>
                      {getStatusBadge(credit.status)}
                      <span className="text-xl font-bold text-emerald-600 flex items-center">
                        <DollarSign className="w-5 h-5" />
                        {credit.amount}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {credit.customer_email || credit.customer_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {credit.property_address}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Created: {new Date(credit.created_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Expires: {new Date(credit.expires_at).toLocaleDateString()}
                      </span>
                    </div>
                    {credit.status === "used" && credit.used_on_address && (
                      <p className="text-xs text-blue-600 mt-2">
                        Used on: {credit.used_on_address}
                      </p>
                    )}
                  </div>
                  {credit.status === "active" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => expireCredit(credit.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`expire-credit-${credit.id}`}
                    >
                      Expire
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
